import { ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Layout } from "@/components/layout/Layout";
import { SEOHead } from "@/components/SEOHead";
import { AnchorPageHero } from "@/components/anchor/AnchorPageHero";
import { useHeroOffset } from "@/hooks/useHeroOffset";
import { LogoBar } from "@/components/anchor/LogoBar";
import { CTAStrip } from "@/components/anchor/CTAStrip";
import { WhySection } from "@/components/anchor/WhySection";
import { ContentSections, ContentSection } from "@/components/anchor/ContentSections";
import { usePersonaReorder } from "@/hooks/usePersonaReorder";

/**
 * Wraps a hand-coded page route. If a published page_settings row exists for the slug
 * AND its overlay mode is "full", the CMS-driven anchor template renders instead of
 * the hand-coded fallback.
 *
 * "header-only" mode is reserved for pages with custom interactive widgets — those
 * keep their hand-coded body but read SEO meta from the CMS row when present.
 */
export function CMSOverlayPage({ slug, children }: { slug: string; children: ReactNode }) {
  // Guardrail: these slugs have hand-coded interactive bodies (filters, calculators,
  // grids) that must never be replaced by the generic anchor template — even if an
  // admin saves Page Settings without explicitly choosing "header-only".
  const FORCE_HEADER_ONLY = new Set<string>([
    "where-to-buy",
    "value-calculator",
    "resources",
    "country-availability",
    "supported-phones",
    "frost-sullivan-award",
    "plans",
    "twenty-years",
    "how-it-works",
    "sims-personal",
    "sims-company",
    "leadership-team",
    "partner-directory",
    "finance",
    "privacy",
    "terms",
    "cookies",
    "acceptable-use-policy",
  ]);

  const { data: settings, isLoading } = useQuery({
    queryKey: ["page-settings-overlay", slug],
    queryFn: async () => {
      const { data } = await supabase
        .from("page_settings")
        .select("*")
        .eq("page_slug", slug)
        .eq("published", true)
        .maybeSingle();
      return data;
    },
  });

  // While loading, render fallback to avoid blank flashes.
  if (isLoading || !settings) return <>{children}</>;

  const rawMode = (settings as any).cms_overlay_mode ?? "full";
  const overlayMode = FORCE_HEADER_ONLY.has(slug) ? "header-only" : rawMode;

  if (overlayMode === "header-only") {
    return (
      <>
        <SEOHead
          title={(settings as any).meta_title || (settings as any).hero_headline || undefined}
          description={(settings as any).meta_description || undefined}
          ogImage={(settings as any).og_image_url || (settings as any).hero_image_url || undefined}
          canonicalPath={(settings as any).canonical_path || undefined}
          noIndex={(settings as any).no_index === true}
        />
        {children}
      </>
    );
  }

  // Full overlay — replace the hand-coded page with the anchor template.
  return <CMSAnchorRender settings={settings} />;
}

function CMSAnchorRender({ settings }: { settings: any }) {
  const { panelRef, onHeroHeight, offsetStyle, offsetClass } = useHeroOffset();
  const ctaButtons = (settings.cta_buttons as string[] | null) ?? [];
  const whyItems = (settings.why_items as string[] | null) ?? [];
  const logoBar = (settings.logo_bar_urls as { url: string; alt: string }[] | null) ?? [];
  const contentSections = ((settings.content_sections ?? []) as unknown as ContentSection[]);
  const subLabel = settings.sub_label ?? null;

  const { reorderedCtas, reorderedSections } = usePersonaReorder(ctaButtons, contentSections);

  return (
    <Layout>
      <SEOHead
        title={settings.meta_title || settings.hero_headline || undefined}
        description={settings.meta_description || undefined}
        ogImage={settings.og_image_url || settings.hero_image_url || undefined}
        canonicalPath={settings.canonical_path || undefined}
        noIndex={settings.no_index === true}
      />
      <AnchorPageHero
        onHeightChange={onHeroHeight}
        imageUrl={settings.hero_image_url}
        headline={settings.hero_headline}
        description={settings.hero_description}
        subLabel={subLabel}
      />
      <div ref={panelRef} className={`relative z-10 ${offsetClass} bg-background rounded-t-3xl shadow-[0_-8px_30px_rgba(0,0,0,0.12)]`} style={offsetStyle}>
        <LogoBar logos={logoBar} />
        {reorderedCtas.length > 0 && <CTAStrip buttons={reorderedCtas} />}
        <ContentSections sections={reorderedSections} />
        {(settings.why_headline || whyItems.length > 0) && (
          <WhySection
            headline={settings.why_headline}
            description={settings.why_description}
            items={whyItems}
          />
        )}
        {reorderedCtas.length > 0 && <CTAStrip buttons={reorderedCtas} variant="dark" />}
      </div>
    </Layout>
  );
}

export default CMSOverlayPage;